import Popover, { positionBounded } from '@components/Popover/Popover';
import Tooltip from '@components/Tooltip/Tooltip';
import { upgradeFlowContext } from '@context/upgrade-flow-context';
import { useUser } from '@context/user-context';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { useGlobalModal } from '@modules/global-modals/use-global-modal';
import { useContext, useEffect, useState } from 'react';
import type { ColorResult } from 'react-color';
import { ChromePicker } from 'react-color';

import styles from './ColorSelector.module.scss';

interface ColorSelectorProps {
  color: string | undefined;
  onSelect: (color: string) => void;
  onCustomColorClicked?: () => void;
  onCustomColorClickedFreeUser?: () => void;
  disabled?: boolean;
  preventCustomColorPopoverInteractionBubbling?: boolean;
}

export const COLORS = {
  RED: '#FF6552',
  ORANGE: '#FB9241',
  YELLOW: '#FAC000',
  GOLD: '#C8A358',
  GREEN: '#3DB512',
  LIGHT_BLUE: '#00E0BB',
  BLUE: '#0F9BFF',
  DARK_BLUE: '#3A4AF8',
  PURPLE: '#A53DF5',
  PINK: '#EB47DA',
  BLACK: '#35393B',
  GREY: '#ACB9BE',
};

function isCustomColor(color: string | undefined) {
  if (!color || Object.values(COLORS).includes(color)) return false;
  return true;
}

const ColorSelector: React.FC<React.PropsWithChildren<ColorSelectorProps>> = ({
  color,
  onSelect,
  disabled = false,
  onCustomColorClicked,
  preventCustomColorPopoverInteractionBubbling,
  onCustomColorClickedFreeUser,
}) => {
  const [customColorNode, customColorRef] = useNode<HTMLElement>();
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(color);

  useEffect(() => {
    if (color) setCustomColor(color);
  }, [color]);

  function handleSelectCustom() {
    setShowCustomColorPicker(true);
    onCustomColorClicked && onCustomColorClicked();
  }

  function handleCustomColorSelect(color: ColorResult) {
    onSelect(color.hex.toUpperCase());
  }

  return (
    <div className={styles['color-selector-container']}>
      <ul className={styles['color-presets']}>
        <li ref={customColorRef}>
          <CustomColor
            selectedHex={color}
            onClick={handleSelectCustom}
            onClickFreeUser={onCustomColorClickedFreeUser}
            selected={isCustomColor(color)}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.RED}
            onClick={onSelect}
            selected={color === COLORS.RED}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.ORANGE}
            onClick={onSelect}
            selected={color === COLORS.ORANGE}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.YELLOW}
            onClick={onSelect}
            selected={color === COLORS.YELLOW}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.GOLD}
            onClick={onSelect}
            selected={color === COLORS.GOLD}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.GREEN}
            onClick={onSelect}
            selected={color === COLORS.GREEN}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.LIGHT_BLUE}
            onClick={onSelect}
            selected={color === COLORS.LIGHT_BLUE}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.BLUE}
            onClick={onSelect}
            selected={color === COLORS.BLUE}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.DARK_BLUE}
            onClick={onSelect}
            selected={color === COLORS.DARK_BLUE}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.PURPLE}
            onClick={onSelect}
            selected={color === COLORS.PURPLE}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.PINK}
            onClick={onSelect}
            selected={color === COLORS.PINK}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.BLACK}
            onClick={onSelect}
            selected={color === COLORS.BLACK}
            disabled={disabled}
          />
        </li>
        <li>
          <Color
            hex={COLORS.GREY}
            onClick={onSelect}
            selected={color === COLORS.GREY}
            disabled={disabled}
          />
        </li>
      </ul>

      <Popover
        positioner={positionBounded({ offsetY: 8 })}
        targetNode={customColorNode}
        show={showCustomColorPicker}
        onClose={() => setShowCustomColorPicker(false)}
        preventOutsideInteraction
        preventInteractionBubbling={preventCustomColorPopoverInteractionBubbling}
      >
        <ChromePicker
          color={customColor}
          onChange={(color) => setCustomColor(color.hex)}
          onChangeComplete={handleCustomColorSelect}
          disableAlpha
        />
      </Popover>
    </div>
  );
};

interface CommonColorProps {
  selected?: boolean;
}

interface ColorProps extends CommonColorProps {
  hex: string;
  disabled?: boolean;
  onClick: (color: string) => void;
  variant?: 'border';
  size?: 'lg';
}

interface CustomColorProps extends CommonColorProps {
  selectedHex: string | undefined;
  disabled: boolean;
  onClick: () => void;
  onClickFreeUser?: () => void;
}

export const Color: React.FC<React.PropsWithChildren<ColorProps>> = ({
  hex,
  onClick,
  selected,
  disabled,
  variant,
  size = 'sm',
}) => {
  const isWhite = hex.toLowerCase().replace('#', '') === 'ffffff';
  const outline = isWhite ? '#555555' : hex;

  return (
    <button
      className={styles['color']}
      data-variant={variant}
      data-size={size}
      onClick={() => onClick(hex)}
      style={{ '--color': hex, '--outline': outline }}
      data-selected={selected}
      disabled={disabled}
      type="button"
    ></button>
  );
};

/**
 *
 * @param selectedHex
 * @param onClick - onclick handler, triggered when the user has permissions to set a custom color
 * @param selected
 * @param disabled
 * @param onClickFreeUser - triggered when the a free user clicks the custom color button
 * @returns
 */
export const CustomColor: React.FC<React.PropsWithChildren<CustomColorProps>> = ({
  selectedHex,
  onClick,
  selected,
  disabled,
  onClickFreeUser,
}) => {
  const { user, userTags } = useUser();
  const [customColorNode, customColorRef] = useNode<HTMLElement>();
  const { openModal, currentModal } = useGlobalModal({
    registeredModals: ['UPGRADE'],
  });
  const [showUpgradeTooltip, setShowUpgradeTooltip] = useState(false);
  const { flowType } = useContext(upgradeFlowContext);

  const canUse =
    flowType === 'onboarding' ? userTags?.canAddCustomColor : !user || userTags?.canAddCustomColor;

  function handleClick() {
    if (canUse) onClick();
    else {
      onClickFreeUser?.();
      if (flowType === 'in-app') {
        openModal({
          type: 'UPGRADE',
          trackingSource: 'custom-color',
        });
        return;
      }
      if (flowType === 'onboarding') {
        setShowUpgradeTooltip(true);
        return;
      }
    }
  }

  return (
    <>
      <Tooltip
        size="small"
        label="Upgrade to a Blinq Premium or Blinq Business account to access custom colors"
        aria-label="Feature lock message"
        show={!canUse && !currentModal}
        ref={customColorRef}
      >
        <span>
          <button
            className={styles['custom-color']}
            onClick={handleClick}
            style={{ '--color': selectedHex }}
            data-selected={selected}
            data-locked={!canUse}
            ref={customColorRef}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faCrown} className={styles['lock-icon']} />
          </button>
        </span>
      </Tooltip>
      {flowType === 'onboarding' && (
        <Popover
          positioner={positionBounded({
            offsetX: 8,
            offsetY: 8,
            xPreference: 'right',
            yPreference: 'bottom',
          })}
          targetNode={customColorNode}
          show={showUpgradeTooltip}
          onClose={() => setShowUpgradeTooltip(false)}
        >
          <div className={styles['custom-color-upgrade-popover']}>
            <h3 className={styles['upgrade-popover-header']}>
              You&apos;ve found a premium feature!
            </h3>
            <p className={styles['upgrade-popover-text']}>
              After you have finished onboarding you can upgrade your account in the dashboard to
              get access to custom colors.
            </p>
          </div>
        </Popover>
      )}
    </>
  );
};

export default ColorSelector;
